"use client";

import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui";
import { Check, ChevronDown } from "lucide-react";
import * as React from "react";
import RPNInputComponent, * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import { cn } from "@/lib/utils";

type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value"
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange?: (value: RPNInput.Value | undefined) => void;
  };

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
  React.forwardRef<React.ElementRef<typeof RPNInputComponent>, PhoneInputProps>(
    ({ className, onChange, ...props }, ref) => {
      return (
        <RPNInputComponent
          ref={ref}
          className={cn("rounded-xlg flex", className)}
          flagComponent={FlagComponent}
          countrySelectComponent={CountrySelectComponent}
          inputComponent={InputComponent}
          onChange={(value) => {
            onChange?.(value);
          }}
          {...props}
        />
      );
    },
  );

PhoneInput.displayName = "PhoneInput";

const InputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentPropsWithoutRef<typeof Input>
>(({ className, ...props }, ref) => (
  <Input
    className={cn(
      "h-9 rounded-s-none rounded-e-lg border-l-0",
      "text-foreground!",
      "focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
      className,
    )}
    {...props}
    ref={ref}
  />
));

InputComponent.displayName = "InputComponent";

interface CountrySelectOption {
  label: string;
  value: RPNInput.Country;
}

interface CountrySelectProps {
  disabled?: boolean;
  value: RPNInput.Country;
  onChange: (value: RPNInput.Country) => void;
  options: CountrySelectOption[];
}

const CountrySelectComponent = ({
  disabled,
  value,
  onChange,
  options,
}: CountrySelectProps) => {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const searchRef = React.useRef<HTMLInputElement>(null);

  const filteredOptions = React.useMemo(() => {
    // US first, then alphabetically.
    const byCountry = (a: CountrySelectOption, b: CountrySelectOption) => {
      if (a.value === "US") {
        return -1;
      }
      if (b.value === "US") {
        return 1;
      }
      return a.label.localeCompare(b.label);
    };

    const validOptions = options.filter((x) => x.value);
    // People type the calling code the way the list renders it ("+44"), but the
    // library stores it bare.
    const term = searchTerm.trim().replace(/^\+/, "").toLowerCase();

    if (!term) {
      return validOptions.sort(byCountry);
    }

    return validOptions
      .filter(
        (option) =>
          option.label.toLowerCase().includes(term) ||
          RPNInput.getCountryCallingCode(option.value).includes(term),
      )
      .sort(byCountry);
  }, [options, searchTerm]);

  const handleSelect = React.useCallback(
    (selectedCountry: RPNInput.Country) => {
      onChange(selectedCountry);
      setOpen(false);
      setSearchTerm("");
    },
    [onChange],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSearchTerm("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "flex h-9 gap-1 rounded-s-lg rounded-e-none px-3",
            "bg-muted/50! border-input!",
            "hover:bg-muted/80!",
            "focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
            "transition-[background-color,opacity] duration-200 ease-out",
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          )}
          disabled={disabled}
          aria-label="Select country"
        >
          <FlagComponent country={value} countryName={value} />
          <ChevronDown
            className={cn(
              "size-4 transition-transform duration-200",
              open && "rotate-180",
              disabled ? "hidden" : "opacity-70",
            )}
          />
        </Button>
      </PopoverTrigger>

      {/* Portaled, so the panel escapes the `.PhoneInput` box. Callers style
          that box freely: `overflow: hidden` on /join clipped every option
          away, and their descendant `input` rules restyled the search field. */}
      <PopoverContent
        align="start"
        className="w-[300px] overflow-hidden p-0"
        onOpenAutoFocus={(event) => {
          // Radix parks focus on the panel itself; the search field is the only
          // thing here worth typing into.
          event.preventDefault();
          searchRef.current?.focus();
        }}
      >
        <div className="border-muted border-b p-2">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search country..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            className="placeholder:text-muted-foreground/60 focus:ring-ring bg-background w-full rounded-sm border px-3 py-1.5 text-sm focus:ring-2 focus:outline-hidden"
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {filteredOptions.length === 0 ? (
            <div className="text-muted-foreground px-2 py-1.5 text-sm">
              No country found.
            </div>
          ) : (
            filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className="focus-visible:bg-accent focus-visible:text-accent-foreground hover:bg-accent hover:text-accent-foreground relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-2 pl-2 text-left text-sm outline-hidden transition-colors"
                onClick={() => {
                  handleSelect(option.value);
                }}
              >
                <FlagComponent
                  country={option.value}
                  countryName={option.label}
                />
                <span className="flex-1">{option.label}</span>
                <span className="text-muted-foreground text-xs">
                  +{RPNInput.getCountryCallingCode(option.value)}
                </span>
                {option.value === value && <Check className="size-4" />}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="flex h-4 w-6 overflow-hidden">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};
FlagComponent.displayName = "FlagComponent";

export { PhoneInput };
